import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Video, X, RotateCcw, Trash2, Share2, CameraOff, Loader2 } from "lucide-react";
import { TIER_META, avatarSvgUri, avatarBg } from "../lib/api";

const RECORD_MS = 8000;
const OUT_W = 720;
const OUT_H = 1280;

// Pick the best-supported recording container/codec across browsers.
const pickMime = () => {
  const candidates = [
    "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
    "video/mp4",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
};

const extFor = (mime) => (mime.includes("mp4") ? "mp4" : "webm");

const handleOf = (item) => item.roaster_handle || ("@" + (item.roaster_name || "roaster").replace(/[^a-zA-Z0-9]/g, "").toLowerCase().slice(0, 20));

const wrapLines = (ctx, text, maxW) => {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  return lines;
};

export default function ReactionRecorder({ open, item, onClose }) {
  const [stage, setStage] = useState("loading"); // loading|ready|denied|countdown|recording|preview|compositing
  const [count, setCount] = useState(3);
  const [rerecorded, setRerecorded] = useState(false);
  const [rawUrl, setRawUrl] = useState(null);

  const liveRef = useRef(null);      // live camera <video>
  const previewRef = useRef(null);   // playback <video> for preview
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("");
  const stopTimerRef = useRef(null);
  const avatarImgRef = useRef(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  const cleanupRaw = useCallback(() => {
    // Drop raw footage the moment it's no longer needed (privacy).
    if (rawUrl) { URL.revokeObjectURL(rawUrl); }
    setRawUrl(null);
    chunksRef.current = [];
  }, [rawUrl]);

  const hardClose = useCallback(() => {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch (e) {}
    stopStream();
    if (rawUrl) URL.revokeObjectURL(rawUrl);
    chunksRef.current = [];
    onClose();
  }, [onClose, rawUrl, stopStream]);

  // Acquire camera on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStage("loading"); setRerecorded(false); setRawUrl(null); setCount(3);
    mimeRef.current = pickMime();

    // preload roaster avatar for the corner credit
    const uri = avatarSvgUri(item?.roaster_avatar);
    if (uri) { const im = new Image(); im.onload = () => { avatarImgRef.current = im; }; im.src = uri; }
    else { avatarImgRef.current = null; }

    (async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === "undefined") {
        if (!cancelled) setStage("denied");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 1280 } },
          audio: true,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (liveRef.current) { liveRef.current.srcObject = stream; liveRef.current.play().catch(() => {}); }
        setStage("ready");
      } catch (e) {
        if (!cancelled) setStage("denied");
      }
    })();

    return () => { cancelled = true; };
  }, [open, item]);

  // release camera whenever the modal closes
  useEffect(() => { if (!open) { stopStream(); } }, [open, stopStream]);

  const beginCountdown = () => {
    setStage("countdown"); setCount(3);
    let n = 3;
    const tick = () => {
      n -= 1;
      if (n <= 0) { startRecording(); }
      else { setCount(n); stopTimerRef.current = setTimeout(tick, 1000); }
    };
    stopTimerRef.current = setTimeout(tick, 1000);
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) { setStage("denied"); return; }
    chunksRef.current = [];
    let rec;
    try { rec = mimeRef.current ? new MediaRecorder(stream, { mimeType: mimeRef.current }) : new MediaRecorder(stream); }
    catch (e) { try { rec = new MediaRecorder(stream); } catch (e2) { setStage("denied"); return; } }
    recRef.current = rec;
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunksRef.current.push(ev.data); };
    rec.onstop = () => {
      const type = mimeRef.current || (chunksRef.current[0] && chunksRef.current[0].type) || "video/webm";
      const blob = new Blob(chunksRef.current, { type });
      const url = URL.createObjectURL(blob);
      setRawUrl(url);
      setStage("preview");
    };
    rec.start();
    setStage("recording");
    stopTimerRef.current = setTimeout(() => { try { if (rec.state !== "inactive") rec.stop(); } catch (e) {} }, RECORD_MS);
  };

  const stopEarly = () => {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch (e) {}
  };

  const rerecord = () => {
    if (rerecorded) return;
    setRerecorded(true);
    cleanupRaw();
    setStage("ready");
  };

  const discard = () => {
    cleanupRaw();
    hardClose();
    toast("Reaction discarded.");
  };

  const shareBlob = async (blob, ext) => {
    const file = new File([blob], `roast-reaction.${ext}`, { type: blob.type });
    const text = "My reaction to getting roasted 🔥 roastlit.app";
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], text, title: "RoastLit" }); return; }
      catch (e) { return; } // user dismissed
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 6000);
    toast.success("Reaction saved — post it to your story! 🔥");
  };

  const drawFrame = (ctx, video, elapsed, total) => {
    const meta = TIER_META[item.tier] || TIER_META.savage;
    ctx.clearRect(0, 0, OUT_W, OUT_H);
    // base: camera, cover-cropped + mirrored
    const vw = video.videoWidth || 720, vh = video.videoHeight || 1280;
    const scale = Math.max(OUT_W / vw, OUT_H / vh);
    const dw = vw * scale, dh = vh * scale;
    ctx.save();
    ctx.translate(OUT_W, 0); ctx.scale(-1, 1);
    ctx.drawImage(video, (OUT_W - dw) / 2, (OUT_H - dh) / 2, dw, dh);
    ctx.restore();
    // legibility gradient bottom
    const g = ctx.createLinearGradient(0, OUT_H * 0.45, 0, OUT_H);
    g.addColorStop(0, "rgba(0,0,0,0)"); g.addColorStop(1, "rgba(0,0,0,0.85)");
    ctx.fillStyle = g; ctx.fillRect(0, OUT_H * 0.45, OUT_W, OUT_H * 0.55);

    // wordmark top-left
    ctx.fillStyle = "#ff4500"; ctx.fillRect(40, 46, 20, 20);
    ctx.fillStyle = "#fff"; ctx.font = "700 30px Oswald, sans-serif"; ctx.textBaseline = "top";
    ctx.fillText("ROASTLIT", 72, 44);

    // roaster credit top-right (chosen avatar, never a photo)
    const cx = OUT_W - 46, cy = 58, r = 26;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = avatarBg(item.roaster_avatar); ctx.fill();
    if (avatarImgRef.current) {
      ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
      ctx.drawImage(avatarImgRef.current, cx - r, cy - r, r * 2, r * 2); ctx.restore();
    }
    ctx.fillStyle = "#fff"; ctx.font = "600 22px Manrope, sans-serif"; ctx.textAlign = "right"; ctx.textBaseline = "middle";
    ctx.fillText(handleOf(item), cx - r - 12, cy);
    ctx.textAlign = "left"; ctx.textBaseline = "top";

    // roast caption (word-by-word reveal), themed lower third
    const words = (item.roast_text || "").split(/\s+/);
    const shown = Math.max(1, Math.min(words.length, Math.ceil((elapsed / (total * 0.7)) * words.length)));
    const text = words.slice(0, shown).join(" ");
    ctx.font = "700 44px Manrope, sans-serif";
    const lines = wrapLines(ctx, text, OUT_W - 100);
    const lh = 56;
    const blockH = lines.length * lh;
    const panelTop = OUT_H - 260 - blockH;
    // tier accent bar
    ctx.fillStyle = meta.color; ctx.fillRect(50, panelTop - 26, 70, 8);
    ctx.fillStyle = "#fff";
    lines.forEach((ln, i) => ctx.fillText(ln, 50, panelTop + i * lh));

    // CTA bottom
    ctx.fillStyle = "#a1a1aa"; ctx.font = "700 24px Manrope, sans-serif";
    ctx.fillText("roastlit.app", 50, OUT_H - 70);
    ctx.fillStyle = meta.color; ctx.font = "700 24px Oswald, sans-serif";
    ctx.fillText(meta.label.toUpperCase(), OUT_W - 50 - ctx.measureText(meta.label.toUpperCase()).width, OUT_H - 70);
  };

  const composite = async () => {
    if (!rawUrl) return;
    setStage("compositing");
    try {
      const cvid = document.createElement("video");
      cvid.src = rawUrl; cvid.playsInline = true; cvid.muted = false;
      await new Promise((res, rej) => { cvid.onloadedmetadata = res; cvid.onerror = rej; });
      const dur = isFinite(cvid.duration) && cvid.duration > 0 ? cvid.duration : RECORD_MS / 1000;

      const canvas = document.createElement("canvas");
      canvas.width = OUT_W; canvas.height = OUT_H;
      const ctx = canvas.getContext("2d");

      // combine canvas video track + original audio track (via WebAudio)
      const canvasStream = canvas.captureStream(30);
      let audioCtx = null;
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          audioCtx = new AC();
          await audioCtx.resume().catch(() => {});
          const src = audioCtx.createMediaElementSource(cvid);
          const dest = audioCtx.createMediaStreamDestination();
          src.connect(dest);
          dest.stream.getAudioTracks().forEach((t) => canvasStream.addTrack(t));
        }
      } catch (e) { /* video-only composite */ }

      const outMime = mimeRef.current || "video/webm";
      let rec;
      try { rec = new MediaRecorder(canvasStream, { mimeType: outMime, videoBitsPerSecond: 2500000 }); }
      catch (e) { rec = new MediaRecorder(canvasStream); }
      const outChunks = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) outChunks.push(ev.data); };
      const stopped = new Promise((res) => { rec.onstop = res; });
      rec.start();

      let raf;
      const draw = () => {
        if (cvid.ended || cvid.currentTime >= dur - 0.05) {
          cancelAnimationFrame(raf);
          try { if (rec.state !== "inactive") rec.stop(); } catch (e) {}
          return;
        }
        drawFrame(ctx, cvid, cvid.currentTime, dur);
        raf = requestAnimationFrame(draw);
      };
      await cvid.play();
      draw();
      await stopped;
      if (audioCtx) audioCtx.close().catch(() => {});

      const outBlob = new Blob(outChunks, { type: outMime });
      // privacy: raw footage is no longer needed
      cleanupRaw();
      stopStream();
      await shareBlob(outBlob, extFor(outMime));
      onClose();
    } catch (e) {
      // Fallback: if compositing isn't supported, share the raw reaction so it's not a dead end.
      try {
        const resp = await fetch(rawUrl); const raw = await resp.blob();
        await shareBlob(raw, extFor(mimeRef.current || "video/webm"));
      } catch (e2) { toast.error("Couldn't build the video on this device."); }
      cleanupRaw(); stopStream(); onClose();
    }
  };

  if (!open) return null;
  const meta = TIER_META[item?.tier] || TIER_META.savage;

  return createPortal(
    <div data-testid="reaction-recorder" className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center">
      <button data-testid="reaction-close-btn" onClick={hardClose} aria-label="Close"
        className="absolute top-4 right-4 z-20 text-white/80 hover:text-white p-2 rounded-full bg-black/40">
        <X size={22} />
      </button>

      {/* 9:16 stage */}
      <div className="relative h-full max-h-[100vh] aspect-[9/16] max-w-full bg-zinc-950 overflow-hidden">
        {stage === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-400 gap-3">
            <Loader2 className="animate-spin" size={28} /> Requesting camera…
          </div>
        )}

        {stage === "denied" && (
          <div data-testid="reaction-denied" className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 gap-4">
            <CameraOff className="text-zinc-500" size={40} />
            <p className="text-zinc-200 font-semibold">Camera unavailable</p>
            <p className="text-zinc-500 text-sm">No worries — your branded roast card is still ready to share. You can skip the reaction video entirely.</p>
            <button data-testid="reaction-skip-btn" onClick={hardClose}
              className="mt-2 px-6 py-2.5 rounded-full bg-brand text-black font-bold text-sm">Skip — show my card</button>
          </div>
        )}

        {/* live camera (ready/countdown/recording) */}
        {(stage === "ready" || stage === "countdown" || stage === "recording") && (
          <>
            <video ref={liveRef} autoPlay muted playsInline className="absolute inset-0 w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
            {/* roast text hidden until countdown completes */}
            {stage === "recording" && (
              <div className="absolute inset-x-0 bottom-0 p-6 pb-24 bg-gradient-to-t from-black/85 to-transparent">
                <div className="h-1.5 w-12 mb-3 rounded-full" style={{ background: meta.color }} />
                <p className="text-white font-bold text-2xl leading-snug" data-testid="reaction-roast-text">"{item.roast_text}"</p>
              </div>
            )}

            {stage === "ready" && (
              <div className="absolute inset-x-0 bottom-0 p-6 flex flex-col items-center gap-3 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white/80 text-sm text-center">Hit record — after 3-2-1 your roast is revealed while the camera catches your reaction (8s). Nothing is uploaded.</p>
                <button data-testid="reaction-record-btn" onClick={beginCountdown}
                  className="flex items-center gap-2 px-8 py-3.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors">
                  <Video size={18} /> Record reaction
                </button>
                <button data-testid="reaction-skip-btn" onClick={hardClose} className="text-white/60 hover:text-white text-sm py-1">
                  Skip — just show my roast
                </button>
              </div>
            )}

            {stage === "countdown" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <span data-testid="reaction-countdown" className="font-heading font-bold text-white" style={{ fontSize: 160, lineHeight: 1 }}>{count}</span>
              </div>
            )}

            {stage === "recording" && (
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> REC
                <button data-testid="reaction-stop-btn" onClick={stopEarly} className="ml-1 underline">stop</button>
              </div>
            )}
          </>
        )}

        {/* preview */}
        {stage === "preview" && rawUrl && (
          <>
            <video ref={previewRef} src={rawUrl} autoPlay loop playsInline className="absolute inset-0 w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
            <div className="absolute inset-x-0 bottom-0 p-6 pb-4 bg-gradient-to-t from-black/85 to-transparent">
              <div className="h-1.5 w-12 mb-3 rounded-full" style={{ background: meta.color }} />
              <p className="text-white font-bold text-2xl leading-snug mb-4">"{item.roast_text}"</p>
              <div className="flex items-center gap-2">
                <button data-testid="reaction-use-btn" onClick={composite}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors">
                  <Share2 size={16} /> Use this
                </button>
                <button data-testid="reaction-rerecord-btn" onClick={rerecord} disabled={rerecorded}
                  className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white font-semibold text-sm transition-colors">
                  <RotateCcw size={15} /> {rerecorded ? "Used" : "Re-record"}
                </button>
                <button data-testid="reaction-discard-btn" onClick={discard}
                  className="flex items-center justify-center px-4 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </>
        )}

        {stage === "compositing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-300 gap-3">
            <Loader2 className="animate-spin text-brand" size={30} />
            <p className="font-semibold">Building your reaction clip…</p>
            <p className="text-zinc-500 text-sm">Burning in captions & optimizing for sharing.</p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
